import { useState, useEffect, useCallback } from "react";
import axios, { AxiosRequestConfig } from "axios";

interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

function useAxiosFetch<T = any>(config: AxiosRequestConfig, startLoading = true): FetchState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(startLoading);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios({ ...config, signal });
        setData(response.data);
      } catch (err: any) {
        // Ignore abort errors
        if (!axios.isCancel(err) && err?.name !== "CanceledError") {
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [config],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  return { data, loading, error };
}

export default useAxiosFetch;
